<script lang="ts">
  import { onMount } from 'svelte';
  import { trackCtaClick } from '$lib/utils/analytics';

  // Scoped dashboard theme, shell, and scan panel styles.
  import '$lib/azimuth-live/styles/pi-theme.css';
  import '$lib/azimuth-live/styles/styles.css';
  import '$lib/azimuth-live/styles/scan-panel.css';

  // Hide the built-in hero statement when a page supplies its own.
  export let showIntro = true;

  // The graph client (d3 + elkjs) is heavy and browser-only,
  // so it's dynamically imported in onMount — off the critical path, never run
  // during prerender. The shell markup below renders immediately (prerendered)
  // and fills with live data once mounted.
  onMount(() => {
    // On very small phones the graph is too cramped to be useful; skip the
    // heavy live client (d3/elkjs/SSE) entirely there.
    if (window.matchMedia('(max-width: 360px)').matches) return;

    let teardown: (() => void) | undefined;
    let cancelled = false;
    import('$lib/azimuth-live/main')
      .then(({ mountAzimuthLive }) => {
        if (!cancelled) teardown = mountAzimuthLive();
      })
      .catch((err) => console.error('azimuth-live failed to mount:', err));
    return () => {
      cancelled = true;
      teardown?.();
    };
  });
</script>

<section class="azimuth-live" aria-label="Live Azimuth protocol scan">
  <!-- Hero statement above the live viz. -->
  {#if showIntro}
  <div class="hero-bar">
    <h1 class="hero-title">The <span class="hero-emphasis">Immune System</span> for the Autonomous Enterprise.</h1>
    <p class="hero-sub">TestMachine builds AI agents that attack, defend, and monitor autonomous systems — so the vulnerabilities and threats real attackers would find get caught and contained first.</p>

    <nav class="security-products security-products--hero" aria-label="Explore TestMachine security products">
      <a class="product-card" href="/products/azimuth">
        <span class="product-icon product-icon--gold" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m8 9-4 3 4 3M16 9l4 3-4 3M14 5l-4 14" /></svg></span>
        <strong>Secure your codebase</strong>
        <small>Prove exploitable paths before attackers do.</small>
        <div class="product-built-for">
          <span class="product-built-label">Built for:</span>
          <ul class="product-bullets">
            <li>Pre-launch protocol audits</li>
            <li>Continuous smart contract monitoring</li>
            <li>DeFi protocol security</li>
          </ul>
        </div>
      </a>
      <a class="product-card" href="/products/agis">
        <span class="product-icon product-icon--gold" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="5" y="7" width="14" height="11" rx="3" /><path d="M12 3v4M8 12h.01M16 12h.01M9 16h6" /></svg></span>
        <strong>Secure your agents</strong>
        <small>Map and contain reachable agent outcomes.</small>
        <div class="product-built-for">
          <span class="product-built-label">Built for:</span>
          <ul class="product-bullets">
            <li>AI Agent Treasury Management</li>
            <li>AI Agent wallet security</li>
            <li>Autonomous trading agents</li>
          </ul>
        </div>
      </a>
      <a class="product-card" href="/products/token-custody">
        <span class="product-icon product-icon--gold" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="M9 9.5c0-1 1.3-1.5 3-1.5s3 .6 3 1.7-1.2 1.6-3 1.8-3 .8-3 1.8 1.3 1.7 3 1.7 3-.5 3-1.5M12 6v12" /></svg></span>
        <strong>Secure your listings</strong>
        <small>Continuously classify token behavior and risk.</small>
        <div class="product-built-for">
          <span class="product-built-label">Built for:</span>
          <ul class="product-bullets">
            <li>Token launch security</li>
            <li>Exchange listing due diligence</li>
          </ul>
        </div>
      </a>
    </nav>

    <div class="hero-ctas">
      <a class="hero-cta hero-cta--primary" href="/#contact" onclick={() => trackCtaClick('home_hero_demo', 'Book a Demo', '/#contact')}>Book a Demo</a>
      <a class="hero-cta hero-cta--secondary" href="https://app.testmachine.ai/" target="_blank" rel="noopener noreferrer" onclick={() => trackCtaClick('home_hero_launch_app', 'Community Edition', 'https://app.testmachine.ai/')}>Community Edition</a>
    </div>
  </div>
  {/if}
  <div class="before-demo-slot">
    <slot name="before-demo" />
  </div>
  <div class="demo-heading">
    <span>Live Azimuth</span>
    <h2>See the attacker in action</h2>
  </div>
  <div class="shell">
    <section class="pane graph-pane">
      <div class="graph-wrap">
        <div id="protocol-graph" class="protocol-graph"></div>
        <!-- Decorative red scan sweep; overlays the graph SVG (DOM order). -->
        <div class="graph-scan" aria-hidden="true"></div>
        <!-- Attack agent rendered as a node that docks onto the contract it's
             working on (positioned/tethered by scan-panel.ts). -->
        <div id="attack-ship" class="attack-ship" aria-hidden="true">
          <div class="attack-ship-head"><span class="attack-ship-dot"></span>Azimuth</div>
          <div id="attack-ship-msg" class="attack-ship-msg"></div>
        </div>
        <!-- Intentionally empty: the scan-panel client picks the correct stage
             (Constructing / Loading next protocol / …) and animates it in once
             scan/current resolves, so no placeholder text flashes on load. -->
        <div id="graph-overlay" class="graph-overlay"></div>
        <!-- Active protocol name + graph stats, overlaid bottom-right of the graph. -->
        <div id="scan-title" class="scan-title">Waiting for next protocol…</div>
        <div id="graph-meta" class="graph-meta"></div>
      </div>
    </section>
    <!-- Centered between scans; the graph and its reasoning bubble take over
         once the next scan loads. -->
    <div class="scan-loading" aria-hidden="true">
      <span class="scan-loading-dots"><i></i><i></i><i></i></span>
      <span>Hunting for whales</span>
    </div>
  </div>

</section>
